/* global gapi, process */
import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';

import SocialLoginButtonView from './SocialLoginButtonView';
import {
  socialLoginButtonDefaultProps,
  socialLoginButtonProps,
} from './propTypes';
import AppConfig from '../../../core/AppConfig';
import { defaultErrorMessage, ErrorWithHumanMessage } from '../../errors';
import withTranslations from '../../components/utils/Translations/withTranslations';
import translatePropTypes from '../../translatePropTypes';

const GOOGLE_JS_URL =
  'https://apis.google.com/js/platform.js?onload=initializeGoogleApi';

if (process.env.BROWSER) {
  window.googleApi = new Promise(resolve => {
    window.initializeGoogleApi = resolve;
  });
}

let GoogleApi = null;

@withTranslations
export default class GoogleLoginButton extends React.PureComponent {
  static propTypes = {
    onLoginInitiated: PropTypes.func,
    ...socialLoginButtonProps,
    ...translatePropTypes,
  };

  static defaultProps = {
    onLoginInitiated: null,
    ...socialLoginButtonDefaultProps,
  };

  componentDidMount() {
    if (!GoogleApi) {
      GoogleApi = window.googleApi.then(
        () =>
          new Promise((resolve, reject) =>
            gapi.load('auth2', () => {
              gapi.auth2
                .init({ client_id: window.App.googleClientId })
                .then(resolve, reject);
            }),
          ),
      );
    }
  }

  handleInitiateLogin = async () => {
    const { onLoginInitiated, onLogin, onLoginError, t } = this.props;

    if (typeof onLoginInitiated === 'function') onLoginInitiated();
    const client = await GoogleApi;

    try {
      const googleUser = await client.signIn({ scope: 'email' });
      onLogin(googleUser.getAuthResponse().id_token);
      await client.disconnect();
    } catch (error) {
      let errorMessage = defaultErrorMessage;

      switch (error.error) {
        case 'popup_closed_by_user':
        case 'access_denied':
          errorMessage = t('Login Cancelled');
          break;
        case 'popup_blocked_by_browser':
          errorMessage = t(
            'Google login popup is blocked by browser. Please allow popups to continue.',
          );
          break;
        default:
          break;
      }

      onLoginError(new ErrorWithHumanMessage(errorMessage));
    }
  };

  render() {
    const { disabled, className, titleTemplate } = this.props;
    return (
      <AppConfig.Consumer>
        {({ googleClientId }) => (
          <>
            <Helmet>
              <meta content={googleClientId} name="google-signin-client_id" />
              <script async defer src={GOOGLE_JS_URL} />
            </Helmet>
            <SocialLoginButtonView
              className={className}
              disabled={disabled}
              titleTemplate={titleTemplate}
              type="GOOGLE"
              onClick={this.handleInitiateLogin}
            />
          </>
        )}
      </AppConfig.Consumer>
    );
  }
}
